/**
 * @fileoverview Modal de criar/editar usuários da página /users (admin).
 * Layout em 3 abas no modo Humano (Dados pessoais / Acesso / Atendimento) com
 * header de identidade (avatar + badge), seletor Humano/IA, rodapé fixo com
 * indicador de alterações não salvas e chips removíveis para setores. No modo
 * IA as abas somem (poucos campos) e o conteúdo rola. A lógica de dados
 * (endpoints, payloads, gates) é a mesma do layout anterior.
 */

import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import {
  makeStyles,
  useTheme,
  useMediaQuery,
  Dialog,
  Button,
  TextField,
  MenuItem,
  Menu,
  Input,
  Avatar,
  Chip,
  Switch,
  IconButton,
  Typography,
  Collapse,
} from "@material-ui/core";
import {
  Plus,
  Pencil,
  X,
  Camera,
  User as PersonIcon,
  Sparkles as AIIcon,
  KeyRound,
  Headphones,
  ChevronDown,
  ShieldCheck,
  ShieldOff,
  RotateCcw,
  Check,
  CheckCheck,
} from "lucide-react";
import CircularProgress from "@material-ui/core/CircularProgress";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import UnsavedChangesHint from "../UnsavedChangesHint";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import { isInternalOnly } from "../../utils/accessScope";
import { useBilling } from "../../context/Billing/BillingContext";
import ModernSelect from "../ModernSelect";
import { roleHelp, roleLabel } from "../TeamAccess/labels";
import { compressImageFile } from "../../utils/imageCompression";
import { createModalDesign } from "../../styles/modalDesign";

const MENU_PROPS = {
  disableScrollLock: true,
  MenuListProps: { dense: true },
};

// Chat Interno "travado": a Empresa nos planos sem licença de Chat Interno
// sobrando (seats_internal >= internos_allowed). O teto é o limite do plano com
// os limites ligados (no Starter sem o complemento, zero); sem teto = liberado.
// Espelha a regra do backend (CreateUserService/UpdateUserService).
export const isInternalLocked = (billing) =>
  billing?.status === "planos" &&
  billing?.internos_allowed != null &&
  typeof billing?.seats_internal === "number" &&
  billing.seats_internal >= billing.internos_allowed;

const getInitialFormData = () => ({
  name: "",
  email: "",
  cpf: "",
  password: "",
  profile: "user",
  queueIds: [],
  whatsappId: "",
  allTicket: "disabled",
  profileImage: "",
  jobRoleId: "",
  isAttendant: true,
  accessScope: "full",
  isAI: false,
  aiEnabled: true,
  aiWebhookUrl: "",
  aiActions: {
    canTransferUser: true,
    canTransferQueue: true,
    canResolveTicket: true,
    canSendMessage: true,
  },
  aiQueueIds: [],
  aiAnswerNoQueue: true,
  aiWhatsappIds: [],
});

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    ...createModalDesign(theme),

    // Sobrepõe o mdDialog (520px): ~680px de largura e altura fixa no desktop.
    userDialog: {
      "& .MuiDialog-paper": {
        width: "min(680px, calc(100vw - 32px))",
        [theme.breakpoints.up("sm")]: {
          height: "min(620px, calc(100vh - 48px))",
        },
      },
      "& .MuiDialog-paperFullScreen": {
        width: "100%",
        maxWidth: "100%",
        height: "100%",
        borderRadius: 0,
      },
    },

    // ── Header de identidade ──────────────────────────────────────────────
    userHeader: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(2),
      padding: theme.spacing(2, 3),
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1.5, 2),
      },
    },
    avatarWrapper: {
      position: "relative",
      flexShrink: 0,
    },
    headerAvatar: {
      width: 56,
      height: 56,
      fontSize: 20,
      fontWeight: 600,
      background: dark ? "rgba(91, 124, 255, 0.2)" : "rgba(61, 92, 253, 0.1)",
      color: dark ? "#7c9aff" : "#3D5CFD",
    },
    cameraBtn: {
      position: "absolute",
      bottom: -2,
      right: -2,
      width: 24,
      height: 24,
      borderRadius: "50%",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      border: `2px solid ${dark ? "#14161c" : "#ffffff"}`,
      transition: "transform 150ms ease",
      "&:hover": { transform: "scale(1.1)" },
    },
    headerIdentity: {
      flex: 1,
      minWidth: 0,
    },
    headerNameRow: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      fontSize: "1rem",
      fontWeight: 700,
      color: dark ? "#f8fafc" : "#111827",
      lineHeight: 1.3,
      "& > span:first-child": {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    },
    headerEmail: {
      fontSize: "0.8rem",
      color: dark ? "#94a3b8" : "#64748b",
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
      marginTop: 2,
    },
    roleBadge: {
      flexShrink: 0,
      padding: "2px 8px",
      borderRadius: 999,
      fontSize: 10,
      fontWeight: 700,
      textTransform: "uppercase",
      letterSpacing: "0.06em",
      lineHeight: 1.6,
    },
    roleBadgeAdmin: {
      background: dark ? "rgba(91, 124, 255, 0.14)" : "rgba(61, 92, 253, 0.1)",
      color: dark ? "#7C9BFF" : "#3D5CFD",
      border: dark
        ? "1px solid rgba(91, 124, 255, 0.3)"
        : "1px solid rgba(61, 92, 253, 0.28)",
    },
    roleBadgeUser: {
      background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.05)",
      color: dark ? "#A1A1AA" : "#64748b",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(15, 23, 42, 0.1)",
    },

    // ── Seletor Humano/IA (compacto, abaixo do header) ────────────────────
    typeBar: {
      display: "flex",
      gap: 4,
      padding: theme.spacing(1.25, 3, 0),
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1.25, 2, 0),
      },
    },
    typeSelector: {
      display: "flex",
      flex: 1,
      background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(15, 23, 42, 0.03)",
      borderRadius: 12,
      padding: 3,
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.06)",
    },
    typeButton: {
      flex: 1,
      padding: "7px 12px",
      borderRadius: 9,
      cursor: "pointer",
      transition: "background-color 0.2s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(0.75),
      background: "transparent",
      border: "none",
      fontSize: "0.82rem",
      fontWeight: 600,
      color: dark ? "rgba(255, 255, 255, 0.5)" : "rgba(0, 0, 0, 0.5)",
      "&:hover": {
        background: dark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.03)",
      },
      "&:disabled": { cursor: "not-allowed" },
    },
    typeButtonActive: {
      background: dark ? "#5B7CFF" : "#3D5CFD",
      color: "#FFFFFF",
      "&:hover": {
        background: dark ? "#4A67FD" : "#2A4BDB",
      },
    },

    // ── Abas ──────────────────────────────────────────────────────────────
    tabsBar: {
      display: "flex",
      gap: theme.spacing(1),
      padding: theme.spacing(1.5, 3),
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1.5, 2),
      },
    },
    tab: {
      flex: 1,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: theme.spacing(0.75),
      padding: "9px 12px",
      borderRadius: 12,
      fontSize: "0.85rem",
      fontWeight: 600,
      cursor: "pointer",
      color: dark ? "#94a3b8" : "#64748b",
      background: dark ? "rgba(255, 255, 255, 0.03)" : "rgba(15, 23, 42, 0.02)",
      border: "1px solid transparent",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      whiteSpace: "nowrap",
      "&:hover": {
        background: dark ? "rgba(91, 124, 255, 0.08)" : "rgba(61, 92, 253, 0.05)",
      },
      [theme.breakpoints.down("xs")]: {
        fontSize: "0.75rem",
        padding: "8px 6px",
        "& svg": { display: "none" },
      },
    },
    tabActive: {
      background: dark ? "rgba(91, 124, 255, 0.15)" : "rgba(61, 92, 253, 0.08)",
      border: dark
        ? "1px solid rgba(91, 124, 255, 0.4)"
        : "1px solid rgba(61, 92, 253, 0.28)",
      color: dark ? "#93c5fd" : "#3D5CFD",
    },

    // ── Conteúdo ──────────────────────────────────────────────────────────
    userContent: {
      flex: 1,
      minHeight: 0,
      overflowY: "auto",
      padding: theme.spacing(2.5, 3),
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(2),
      },
    },
    loadingState: {
      padding: theme.spacing(6, 2),
      textAlign: "center",
      color: dark ? "rgba(255, 255, 255, 0.55)" : "#64748b",
    },

    // ── Chips de setores ──────────────────────────────────────────────────
    chipsWrap: {
      display: "flex",
      flexWrap: "wrap",
      alignItems: "center",
      gap: theme.spacing(0.75),
    },
    queueChip: {
      borderRadius: 999,
      background: dark ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.05)",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.1)"
        : "1px solid rgba(15, 23, 42, 0.1)",
      color: dark ? "#e6e8eb" : "#1e293b",
      fontWeight: 500,
      "& .MuiChip-deleteIcon": {
        color: dark ? "rgba(255, 255, 255, 0.4)" : "rgba(15, 23, 42, 0.35)",
        "&:hover": { color: dark ? "#f8fafc" : "#0f172a" },
      },
    },
    chipDot: {
      width: 8,
      height: 8,
      borderRadius: "50%",
      marginLeft: 8,
      flexShrink: 0,
    },
    noQueuesText: {
      fontSize: "0.8rem",
      color: dark ? "#71717A" : "#94a3b8",
    },
    addQueueBtn: {
      height: 28,
      padding: "0 10px",
      borderRadius: 999,
      fontSize: "0.78rem",
      fontWeight: 600,
      textTransform: "none",
      color: dark ? "#7c9aff" : "#3D5CFD",
      border: dark
        ? "1px dashed rgba(91, 124, 255, 0.4)"
        : "1px dashed rgba(61, 92, 253, 0.35)",
      background: "transparent",
      "&:hover": {
        background: dark ? "rgba(91, 124, 255, 0.08)" : "rgba(61, 92, 253, 0.05)",
      },
    },
    queueMenuPaper: {
      borderRadius: 14,
      marginTop: 4,
      background: dark ? "#1c1f26" : "#ffffff",
      border: dark
        ? "1px solid rgba(91, 124, 255, 0.2)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      boxShadow: dark
        ? "0 12px 32px rgba(0, 0, 0, 0.45)"
        : "0 12px 32px rgba(15, 23, 42, 0.12)",
    },
    queueMenuList: {
      padding: 6,
      "& .MuiMenuItem-root": {
        borderRadius: 10,
        fontSize: "0.85rem",
        display: "flex",
        alignItems: "center",
        gap: 8,
        "&:hover": {
          background: dark
            ? "rgba(91, 124, 255, 0.1)"
            : "rgba(61, 92, 253, 0.06)",
        },
      },
    },
    queueMenuItemSelected: {
      background: dark ? "rgba(91, 124, 255, 0.12)" : "rgba(61, 92, 253, 0.07)",
    },
    queueMenuItemLabel: {
      flex: 1,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    queueMenuCheck: {
      color: dark ? "#7c9aff" : "#3D5CFD",
      flexShrink: 0,
    },
    queueMenuAllItem: {
      fontWeight: 600,
      color: dark ? "#7c9aff" : "#3D5CFD",
      marginBottom: 4,
      borderBottom: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.06)",
      borderRadius: "10px 10px 0 0 !important",
      paddingBottom: 10,
    },

    // ── Card MFA (mantido do layout anterior) ─────────────────────────────
    mfaCard: {
      padding: theme.spacing(1.5),
      borderRadius: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(2),
      background: dark ? "rgba(91, 124, 255, 0.06)" : "rgba(61, 92, 253, 0.04)",
      border: dark
        ? "1px solid rgba(91, 124, 255, 0.18)"
        : "1px solid rgba(61, 92, 253, 0.12)",
    },
    mfaCardLeft: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1.5),
      minWidth: 0,
    },
    mfaIconBadge: {
      width: 36,
      height: 36,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
    },
    mfaIconBadgeOn: {
      background: dark ? "rgba(16, 185, 129, 0.18)" : "rgba(16, 185, 129, 0.12)",
      color: dark ? "#34d399" : "#059669",
      border: dark
        ? "1px solid rgba(16, 185, 129, 0.35)"
        : "1px solid rgba(16, 185, 129, 0.25)",
    },
    mfaIconBadgeOff: {
      background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(0, 0, 0, 0.04)",
      color: dark ? "rgba(255, 255, 255, 0.45)" : "#94a3b8",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.08)"
        : "1px solid rgba(0, 0, 0, 0.06)",
    },
    mfaTextWrap: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
    },
    mfaTitle: {
      fontSize: "0.85rem",
      fontWeight: 600,
      color: dark ? "#f1f5f9" : "#1e293b",
      lineHeight: 1.2,
    },
    mfaStatus: {
      fontSize: "0.75rem",
      fontWeight: 500,
      marginTop: 2,
    },
    mfaStatusOn: {
      color: dark ? "#34d399" : "#059669",
    },
    mfaStatusOff: {
      color: dark ? "rgba(255, 255, 255, 0.5)" : "#94a3b8",
    },
    mfaResetBtn: {
      fontSize: "0.72rem",
      fontWeight: 600,
      textTransform: "none",
      borderRadius: 10,
      padding: "6px 12px",
      whiteSpace: "nowrap",
      color: dark ? "#fca5a5" : "#dc2626",
      borderColor: dark ? "rgba(220, 38, 38, 0.35)" : "rgba(220, 38, 38, 0.3)",
      "&:hover": {
        background: dark ? "rgba(220, 38, 38, 0.1)" : "rgba(220, 38, 38, 0.06)",
        borderColor: dark ? "#fca5a5" : "#dc2626",
      },
    },

    // ── Seção IA (de-roxificada: só o azul accent do design system) ───────
    aiSection: {
      padding: theme.spacing(2),
      background: dark ? "rgba(91, 124, 255, 0.06)" : "rgba(61, 92, 253, 0.04)",
      borderRadius: 14,
      border: dark
        ? "1px solid rgba(91, 124, 255, 0.15)"
        : "1px solid rgba(61, 92, 253, 0.1)",
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(1.5),
    },
    switchContainer: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1),
    },
    aiSectionTitle: {
      color: dark ? "#7c9aff" : "#3D5CFD",
      fontWeight: 700,
    },
    switchControl: {
      "& .MuiSwitch-switchBase.Mui-checked": { color: "#5B7CFF" },
      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
        backgroundColor: "#5B7CFF",
        opacity: 0.6,
      },
    },
    codeBlock: {
      background: dark ? "rgba(0, 0, 0, 0.3)" : "rgba(0, 0, 0, 0.04)",
      borderRadius: "12px",
      padding: theme.spacing(2),
      fontFamily: '"Fira Code", "Monaco", monospace',
      fontSize: "0.75rem",
      overflowX: "auto",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(0, 0, 0, 0.06)",
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(2),
      "& pre": {
        margin: 0,
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      },
    },
    docSection: {
      padding: theme.spacing(2),
      background: dark ? "rgba(0, 0, 0, 0.2)" : "rgba(0, 0, 0, 0.02)",
      borderRadius: "12px",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.05)"
        : "1px solid rgba(0, 0, 0, 0.05)",
    },
    docTitle: {
      fontWeight: 600,
      fontSize: "0.85rem",
      marginBottom: theme.spacing(1),
      color: dark ? "rgba(255, 255, 255, 0.7)" : "rgba(0, 0, 0, 0.6)",
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      cursor: "pointer",
      "&:hover": {
        color: dark ? "#5B7CFF" : "#3D5CFD",
      },
    },
    customizeLink: {
      alignSelf: "flex-start",
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: 0,
      border: "none",
      background: "transparent",
      color: dark ? "rgba(255,255,255,0.72)" : "#475569",
      fontSize: 13,
      fontWeight: 500,
      fontFamily: "inherit",
      cursor: "pointer",
      textDecoration: "underline",
      textUnderlineOffset: 3,
      "&:hover": { color: dark ? "#ffffff" : "#0f172a" },
      "&:focus-visible": { outline: "none", boxShadow: `0 0 0 1px ${dark ? "rgba(255,255,255,0.6)" : "rgba(15,23,42,0.6)"}` },
    },
    internalLockedNotice: {
      fontSize: 12,
      lineHeight: 1.4,
      color: "#f59e0b",
      marginTop: -theme.spacing(1),
    },

    // ── Rodapé fixo ───────────────────────────────────────────────────────
    userFooter: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: theme.spacing(1.5),
      padding: theme.spacing(1.75, 3),
      borderTop: dark
        ? "1px solid rgba(255, 255, 255, 0.06)"
        : "1px solid rgba(15, 23, 42, 0.08)",
      background: dark ? "rgba(0, 0, 0, 0.22)" : "rgba(15, 23, 42, 0.02)",
      flexShrink: 0,
      [theme.breakpoints.down("xs")]: {
        padding: theme.spacing(1.5, 2),
      },
    },
    footerDirty: {
      display: "flex",
      alignItems: "center",
      minWidth: 0,
      overflow: "hidden",
    },
    footerActions: {
      display: "flex",
      alignItems: "center",
      gap: theme.spacing(1),
      flexShrink: 0,
    },
    btnBase: {
      height: 38,
      padding: "0 16px",
      borderRadius: 10,
      fontSize: "0.875rem",
      fontWeight: 500,
      textTransform: "none",
      whiteSpace: "nowrap",
      flexShrink: 0,
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      border: "none",
      boxShadow: "none",
      transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
      "& svg": { flexShrink: 0 },
      "& .MuiButton-label": { whiteSpace: "nowrap" },
    },
    btnCancel: {
      background: "transparent",
      border: dark
        ? "1px solid rgba(255, 255, 255, 0.12)"
        : "1px solid rgba(15, 23, 42, 0.12)",
      color: dark ? "rgba(248, 250, 252, 0.72)" : "#6b7280",
      "&:hover": {
        background: dark ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.04)",
        color: dark ? "#f8fafc" : "#111827",
      },
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#ffffff",
      "&:hover": {
        background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
      },
      "&:disabled": { opacity: 0.55, cursor: "not-allowed" },
    },
    spinner: { color: "#ffffff" },
  };
});

const UserEditorModal = ({ open, onClose, onSaved, selectedUser }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const { user: loggedInUser } = useContext(AuthContext);
  // O que cada campo pede, como a API (UserServices/userActions): perfil e
  // licença de quem já existe, team.manage_access (no cadastro, a licença dentro
  // da própria basta); senha e e-mail de outra pessoa e o 2FA,
  // team.reset_credentials; as empresas do grupo, team.manage_companies; o
  // usuário IA, ai_agents.manage. Na regra de hoje, tudo do admin (e do super).
  const { can, enforce } = usePermission();
  const legacyAdmin = isAdminUser(loggedInUser);
  const canManageAccess = can("team.manage_access", legacyAdmin);
  // Com a regra nova, o perfil de acesso é o da área Equipe e permissões: o
  // cadastro mostra os três perfis e grava a troca por ela (com as regras de
  // quem muda o quê). Sem a regra, o admin/user de hoje.
  const accessRoles = enforce && canManageAccess;
  const canResetCredentials = can("team.reset_credentials", legacyAdmin);
  const canManageCompanies = can("team.manage_companies", legacyAdmin);
  const canManageAi = can("ai_agents.manage", legacyAdmin);
  const isSelf = !!selectedUser && Number(selectedUser.id) === Number(loggedInUser?.id);
  const lockCredentials = !!selectedUser && !isSelf && !canResetCredentials;
  const ownLicense = loggedInUser?.accessScope || "full";
  const licenseAllowed = (license) => canManageAccess || ownLicense === "full" || license === ownLicense;
  const { billing } = useBilling();
  // Sem assentos de Chat Interno → trava as opções com escopo interno no seletor.
  const internalLocked = isInternalLocked(billing);

  const [formData, setFormData] = useState(getInitialFormData());
  const [imagePreview, setImagePreview] = useState("");
  const [queues, setQueues] = useState([]);
  const [whatsapps, setWhatsapps] = useState([]);
  const [jobRoles, setJobRoles] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [userCompanyIds, setUserCompanyIds] = useState([]);
  const [userMaxCompanies, setUserMaxCompanies] = useState(1);
  const [showApiDocs, setShowApiDocs] = useState(false);
  const [loadingData, setLoadingData] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [queueMenuAnchor, setQueueMenuAnchor] = useState(null);
  // Com a regra nova, o acesso da pessoa na área de permissões (perfil,
  // exceções e versão) e o perfil escolhido, à parte do formulário; sem ele (o
  // usuário IA, que a área não mostra), fica o seletor de hoje.
  const [memberAccess, setMemberAccess] = useState(null);
  const [accessRole, setAccessRole] = useState("agent");
  const history = useHistory();

  // Snapshot do formData carregado — base do indicador "alterações não salvas".
  // Máx. empresas / Empresas do Grupo ficam fora (auto-salvam no onChange).
  const initialFormRef = useRef(JSON.stringify(getInitialFormData()));

  const updateFormData = (patch) => {
    setFormData((prevState) => ({
      ...prevState,
      ...patch,
    }));
  };

  const queueMenuItems = useMemo(() => {
    if (!queues.length) {
      return [
        <MenuItem key="empty" value="" disabled>
          {i18n.t("userModal.form.noQueueAvailable")}
        </MenuItem>,
      ];
    }
    return queues.map((queue) => (
      <MenuItem key={queue.id} value={queue.id}>
        {queue.name}
      </MenuItem>
    ));
  }, [queues]);

  const whatsappMenuItems = useMemo(() => {
    if (!whatsapps.length) {
      return [
        <MenuItem key="empty" value="" disabled>
          {i18n.t("userModal.form.noConnectionAvailable")}
        </MenuItem>,
      ];
    }
    return whatsapps.map((whatsapp) => (
      <MenuItem key={whatsapp.id} value={whatsapp.id}>
        {whatsapp.name}
      </MenuItem>
    ));
  }, [whatsapps]);

  const jobRoleMenuItems = useMemo(
    () =>
      jobRoles.map((jobRole) => (
        <MenuItem key={jobRole.id} value={jobRole.id}>
          {jobRole.name}
        </MenuItem>
      )),
    [jobRoles]
  );

  const companyMenuItems = useMemo(
    () =>
      availableCompanies.map((company) => (
        <MenuItem key={company.id} value={company.id}>
          {company.name}
        </MenuItem>
      )),
    [availableCompanies]
  );

  const resetFormState = () => {
    const fresh = getInitialFormData();
    setFormData(fresh);
    initialFormRef.current = JSON.stringify(fresh);
    setImagePreview("");
    setAvailableCompanies([]);
    setUserCompanyIds([]);
    setUserMaxCompanies(1);
    setShowApiDocs(false);
    setSaving(false);
    setLoadingData(false);
    setActiveTab("personal");
    setQueueMenuAnchor(null);
    setMemberAccess(null);
    setAccessRole("agent");
  };

  const handleClose = () => {
    resetFormState();
    onClose();
  };

  // "Personalizar permissões": a pessoa em Configurações → Equipe e permissões.
  const openPermissions = () => {
    if (isDirty && !window.confirm(i18n.t("unsavedChanges.leaveConfirm"))) return;
    const userId = selectedUser.id;
    handleClose();
    history.push(`/settings/team-access?user=${userId}`);
  };

  useEffect(() => {
    if (!open) {
      resetFormState();
      return undefined;
    }

    let isMounted = true;

    const loadModalData = async () => {
      setLoadingData(true);

      try {
        // Filas e conexões são do módulo de atendimento: quem tem escopo
        // "Apenas Interno" recebe 403. Com Promise.all uma rejeição derrubaria
        // TODAS as promessas e o modal abriria vazio — sem cargos, inclusive.
        // Por isso pulamos as duas chamadas em vez de deixá-las falhar.
        const canLoadAttendance = !isInternalOnly(loggedInUser);
        const [queuesRes, whatsappsRes, jobRolesRes] = await Promise.all([
          canLoadAttendance ? api.get("/queue") : Promise.resolve({ data: [] }),
          canLoadAttendance ? api.get("/whatsapp") : Promise.resolve({ data: [] }),
          api.get("/job-roles"),
        ]);

        if (!isMounted) {
          return;
        }

        setQueues(queuesRes.data || []);
        setWhatsapps(whatsappsRes.data || []);
        setJobRoles(jobRolesRes.data || []);

        if (!selectedUser?.id) {
          const fresh = getInitialFormData();
          setFormData(fresh);
          initialFormRef.current = JSON.stringify(fresh);
          setImagePreview("");
          setAvailableCompanies([]);
          setUserCompanyIds([]);
          setUserMaxCompanies(1);
          setShowApiDocs(false);
          return;
        }

        const { data } = await api.get(`/users/${selectedUser.id}`);

        if (!isMounted) {
          return;
        }

        const loaded = {
          ...getInitialFormData(),
          name: data.name || "",
          email: data.email || "",
          cpf: data.cpf || "",
          profile: data.profile || "user",
          queueIds: data.queues?.map((queue) => queue.id) || [],
          whatsappId: data.whatsappId || "",
          allTicket: data.allTicket || "disabled",
          profileImage: data.profileImage || "",
          jobRoleId: data.jobRoleId || "",
          isAttendant: data.isAttendant !== undefined ? data.isAttendant : true,
          accessScope: data.accessScope || "full",
          isAI: data.isAI || false,
          aiEnabled: data.aiEnabled !== undefined ? data.aiEnabled : true,
          aiWebhookUrl: data.aiWebhookUrl || "",
          aiActions: data.aiActions || getInitialFormData().aiActions,
          aiQueueIds: data.aiQueueIds || [],
          aiAnswerNoQueue:
            data.aiAnswerNoQueue !== undefined ? data.aiAnswerNoQueue : true,
          aiWhatsappIds: data.aiWhatsappIds || [],
        };
        setFormData(loaded);
        initialFormRef.current = JSON.stringify(loaded);
        setImagePreview(data.profileImage || "");
        setUserMaxCompanies(data.maxCompanies || 1);

        // As empresas do grupo só com team.manage_companies (a API recusa os demais).
        if (!canManageCompanies) {
          setAvailableCompanies([]);
          setUserCompanyIds([]);
          return;
        }
        try {
          const [companiesRes, availableRes] = await Promise.all([
            api.get(`/users/${selectedUser.id}/companies`),
            api.get(`/users/${selectedUser.id}/available-companies`),
          ]);

          if (!isMounted) {
            return;
          }

          setUserCompanyIds((companiesRes.data || []).map((company) => company.id));
          setAvailableCompanies(availableRes.data || []);
        } catch (err) {
          if (!isMounted) {
            return;
          }

          setAvailableCompanies([]);
          setUserCompanyIds([]);
          toastError(err);
        }
      } catch (err) {
        if (isMounted) {
          toastError(err);
        }
      } finally {
        if (isMounted) {
          setLoadingData(false);
        }
      }
    };

    loadModalData();

    return () => {
      isMounted = false;
    };
  }, [open, selectedUser, canManageCompanies]);

  // O acesso da pessoa na área de permissões, lido à parte: a permissão de quem
  // edita que chega (ou é relida) com o cadastro aberto não recarrega o formulário.
  useEffect(() => {
    setMemberAccess(null);
    setAccessRole("agent");
    if (!open || !selectedUser?.id || !accessRoles) return undefined;
    let active = true;
    api
      .get(`/access-control/members/${selectedUser.id}`)
      .then(({ data }) => {
        if (!active) return;
        setMemberAccess(data);
        setAccessRole(data.role);
      })
      .catch(() => {
        if (active) setMemberAccess(null);
      });
    return () => {
      active = false;
    };
  }, [open, selectedUser, accessRoles]);

  // (UX) Em conta sem assentos de Chat Interno, um usuário NOVO já abre como
  // "Apenas Atendimento" (em vez do default "Completo") — evita o admin cair no
  // erro de limite interno ao salvar. Só na criação (não força em edição).
  useEffect(() => {
    if (
      open && !selectedUser && internalLocked &&
      (formData.accessScope === "full" || formData.accessScope === "internal")
    ) {
      updateFormData({ accessScope: "attendance" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedUser, internalLocked]);

  // Sem team.manage_access, o cadastro só cria licença dentro da de quem
  // cadastra (a API recusa a maior): começa nela.
  useEffect(() => {
    if (open && !selectedUser && !licenseAllowed(formData.accessScope)) {
      updateFormData({ accessScope: ownLicense });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectedUser, canManageAccess]);

  // Comprime antes de virar base64: o avatar e gravado inline na coluna TEXT
  // Users.profileImage, e o arquivo cru chegava a MBs por usuario, inchando
  // toda query que le a tabela. 200x200 basta pro tamanho exibido.
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) {
      return;
    }

    try {
      const compressed = await compressImageFile(file, {
        maxWidth: 200,
        maxHeight: 200,
        quality: 0.8
      });
      updateFormData({ profileImage: compressed });
      setImagePreview(compressed);
    } catch (err) {
      toastError(err);
    }
  };

  // A pessoa recém-cadastrada vira Supervisor pela área de permissões (o
  // cadastro cria Atendente). Se falhar, ela fica Atendente e o aviso diz o motivo.
  const promoteToSupervisor = async (userId) => {
    try {
      const { data: detail } = await api.get(`/access-control/members/${userId}`);
      await api.put(`/access-control/members/${userId}`, { role: "supervisor", overrides: [], version: detail.version });
    } catch (err) {
      toastError(err);
    }
  };

  const handleSaveUser = async () => {
    setSaving(true);

    try {
      const payload = {
        ...formData,
        jobRoleId: formData.jobRoleId || null,
      };

      if (selectedUser?.id) {
        // Com a regra nova, o perfil vai pela área de permissões, antes do resto
        // (com a versão lida), e o cadastro não grava o perfil antigo. Gravado o
        // perfil, fica a versão nova: se o resto falhar, salvar de novo não o repete.
        if (memberAccess && accessRole !== memberAccess.role) {
          const { data: saved } = await api.put(`/access-control/members/${selectedUser.id}`, {
            role: accessRole,
            overrides: memberAccess.overrides,
            version: memberAccess.version,
          });
          setMemberAccess(saved);
        }
        const { profile, ...withoutProfile } = payload;
        await api.put(`/users/${selectedUser.id}`, memberAccess ? withoutProfile : payload);
        toast.success(i18n.t("users.toasts.updated"));
      } else {
        const { data: created } = await api.post(
          "/users",
          accessRoles ? { ...payload, profile: accessRole === "admin" ? "admin" : "user" } : payload
        );
        toast.success(i18n.t("users.toasts.created"));
        if (accessRoles && accessRole === "supervisor" && created?.id) await promoteToSupervisor(created.id);
      }

      if (onSaved) {
        await onSaved();
      }

      handleClose();
    } catch (err) {
      toastError(err);
      setSaving(false);
    }
  };

  const handleMaxCompaniesChange = async (event) => {
    if (!selectedUser?.id) {
      return;
    }

    const value = Math.max(1, parseInt(event.target.value, 10) || 1);
    setUserMaxCompanies(value);

    try {
      await api.put(`/users/${selectedUser.id}`, { maxCompanies: value });
    } catch (err) {
      toastError(err);
    }
  };

  const handleCompaniesChange = async (event) => {
    if (!selectedUser?.id) {
      return;
    }

    const newIds = event.target.value;
    const added = newIds.filter((id) => !userCompanyIds.includes(id));
    const removed = userCompanyIds.filter((id) => !newIds.includes(id));

    try {
      await Promise.all([
        ...added.map((companyId) =>
          api.post(`/users/${selectedUser.id}/companies`, { companyId })
        ),
        ...removed.map((companyId) =>
          api.delete(`/users/${selectedUser.id}/companies/${companyId}`)
        ),
      ]);

      setUserCompanyIds(newIds);
    } catch (err) {
      toastError(err);

      try {
        const { data } = await api.get(`/users/${selectedUser.id}/companies`);
        setUserCompanyIds((data || []).map((company) => company.id));
      } catch (reloadErr) {
        toastError(reloadErr);
      }
    }
  };

  const isDirty =
    JSON.stringify(formData) !== initialFormRef.current || (!!memberAccess && accessRole !== memberAccess.role);

  // Zerar o 2FA de um administrador (que não a própria pessoa) pede também team.manage_access.
  const showMfaCard =
    !!selectedUser && canResetCredentials && (selectedUser.profile !== "admin" || isSelf || canManageAccess);
  const showMaxCompanies = !!selectedUser && !!loggedInUser?.super;
  const showGroupCompanies = !!selectedUser && availableCompanies.length > 0 && canManageCompanies;

  const tabs = [
    { id: "personal", icon: PersonIcon, label: i18n.t("userModal.tabs.personal") },
    { id: "access", icon: KeyRound, label: i18n.t("userModal.tabs.access") },
    { id: "service", icon: Headphones, label: i18n.t("userModal.tabs.service") },
  ];

  const optional = i18n.t("userModal.form.optionalSuffix");
  // Com a regra nova, o selo e o perfil vêm do perfil de acesso (o Supervisor também).
  const showsAccessRole = accessRoles && (!!memberAccess || !selectedUser);
  const isAdminProfile = showsAccessRole ? accessRole === "admin" : formData.profile === "admin";
  const selectedQueues = formData.queueIds.map((id) => ({
    id,
    queue: queues.find((q) => q.id === id),
  }));
  const allQueuesSelected =
    queues.length > 0 && queues.every((q) => formData.queueIds.includes(q.id));

  // Toggle com functional update: o menu fica aberto para seleção múltipla,
  // então cliques rápidos não podem depender de um formData de closure stale.
  const toggleQueueId = (id) => {
    setFormData((prev) => ({
      ...prev,
      queueIds: prev.queueIds.includes(id)
        ? prev.queueIds.filter((x) => x !== id)
        : [...prev.queueIds, id],
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      className={`${classes.mdDialog} ${classes.userDialog}`}
      maxWidth={false}
      fullScreen={isMobile}
    >
      {/* ── Header: identidade de quem está sendo editado ── */}
      <div className={classes.userHeader}>
        <div className={classes.avatarWrapper}>
          <Avatar
            src={imagePreview || formData.profileImage || undefined}
            className={classes.headerAvatar}
          >
            {formData.isAI ? (
              <AIIcon size={24} strokeWidth={1.75} />
            ) : (
              (formData.name || "?").charAt(0).toUpperCase()
            )}
          </Avatar>
          <Input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            style={{ display: "none" }}
            id="user-editor-profile-image-upload"
          />
          <label
            htmlFor="user-editor-profile-image-upload"
            className={classes.cameraBtn}
            title={i18n.t("userModal.form.changePhoto")}
            aria-label={i18n.t("userModal.form.changePhoto")}
          >
            <Camera size={12} strokeWidth={2} />
          </label>
        </div>
        <div className={classes.headerIdentity}>
          <div className={classes.headerNameRow}>
            <span>
              {formData.name ||
                (selectedUser
                  ? i18n.t("userModal.title.edit")
                  : i18n.t("userModal.title.add"))}
            </span>
            <span
              className={`${classes.roleBadge} ${
                formData.isAI || isAdminProfile
                  ? classes.roleBadgeAdmin
                  : classes.roleBadgeUser
              }`}
            >
              {formData.isAI
                ? "IA"
                : showsAccessRole
                ? roleLabel(accessRole)
                : i18n.t(isAdminProfile ? "users.table.admin" : "users.table.user")}
            </span>
          </div>
          {formData.email && !formData.isAI && (
            <div className={classes.headerEmail}>{formData.email}</div>
          )}
        </div>
        <IconButton size="small" onClick={handleClose} className={classes.mdCloseBtn}>
          <X size={18} strokeWidth={1.75} />
        </IconButton>
      </div>

      {loadingData ? (
        <div className={classes.userContent}>
          <div className={classes.loadingState}>{i18n.t("userModal.loading")}</div>
        </div>
      ) : (
        <>
          {/* ── Seletor Humano/IA ── */}
          <div className={classes.typeBar}>
            <div className={classes.typeSelector}>
              <button
                type="button"
                className={`${classes.typeButton} ${
                  !formData.isAI ? classes.typeButtonActive : ""
                }`}
                onClick={() => updateFormData({ isAI: false })}
                disabled={!canManageAi}
              >
                <PersonIcon size={16} strokeWidth={1.8} />
                Humano
              </button>
              <button
                type="button"
                className={`${classes.typeButton} ${
                  formData.isAI ? classes.typeButtonActive : ""
                }`}
                onClick={() => updateFormData({ isAI: true })}
                disabled={!canManageAi}
              >
                <AIIcon size={16} strokeWidth={1.8} />
                IA
              </button>
            </div>
          </div>

          {/* ── Abas (só no modo Humano; IA tem poucos campos) ── */}
          {!formData.isAI && (
            <div className={classes.tabsBar}>
              {tabs.map((t) => {
                const TabIcon = t.icon;
                return (
                  <div
                    key={t.id}
                    className={`${classes.tab} ${
                      activeTab === t.id ? classes.tabActive : ""
                    }`}
                    onClick={() => setActiveTab(t.id)}
                    role="tab"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setActiveTab(t.id)}
                  >
                    <TabIcon size={16} strokeWidth={1.8} />
                    {t.label}
                  </div>
                );
              })}
            </div>
          )}

          {/* ── Conteúdo ── */}
          <div className={classes.userContent}>
            {formData.isAI ? (
              <>
                <TextField
                  label="Nome da IA"
                  variant="outlined"
                  value={formData.name}
                  onChange={(event) => updateFormData({ name: event.target.value })}
                  fullWidth
                  className={classes.mdField}
                />

                <div className={classes.aiSection}>
                  <div className={classes.switchContainer}>
                    <Typography variant="body2" className={classes.aiSectionTitle}>
                      IA Ativa
                    </Typography>
                    <Switch
                      size="small"
                      className={classes.switchControl}
                      checked={formData.aiEnabled}
                      onChange={(event) =>
                        updateFormData({ aiEnabled: event.target.checked })
                      }
                      disabled={!canManageAi}
                    />
                  </div>

                  <TextField
                    label="URL do Webhook"
                    variant="outlined"
                    value={formData.aiWebhookUrl}
                    onChange={(event) =>
                      updateFormData({ aiWebhookUrl: event.target.value })
                    }
                    disabled={!canManageAi}
                    fullWidth
                    className={classes.mdField}
                    placeholder="https://seu-n8n.com/webhook/..."
                    helperText="Endpoint que receberá as mensagens"
                  />

                  <div className={classes.mdFieldRow}>
                    <ModernSelect
                      label="Filas"
                      multiple
                      value={formData.aiQueueIds}
                      onChange={(event) =>
                        updateFormData({ aiQueueIds: event.target.value })
                      }
                      disabled={!canManageAi}
                      renderValue={(selected) =>
                        selected.length === 0
                          ? "Todas"
                          : queues
                              .filter((queue) => selected.includes(queue.id))
                              .map((queue) => queue.name)
                              .join(", ")
                      }
                      MenuProps={MENU_PROPS}
                      formControlProps={{ className: classes.mdField }}
                    >
                      {queueMenuItems}
                    </ModernSelect>

                    <ModernSelect
                      label="Conexões"
                      multiple
                      value={formData.aiWhatsappIds}
                      onChange={(event) =>
                        updateFormData({ aiWhatsappIds: event.target.value })
                      }
                      disabled={!canManageAi}
                      renderValue={(selected) =>
                        selected.length === 0
                          ? "Todas"
                          : whatsapps
                              .filter((whatsapp) => selected.includes(whatsapp.id))
                              .map((whatsapp) => whatsapp.name)
                              .join(", ")
                      }
                      MenuProps={MENU_PROPS}
                      formControlProps={{ className: classes.mdField }}
                    >
                      {whatsappMenuItems}
                    </ModernSelect>
                  </div>

                  <div className={classes.switchContainer}>
                    <Typography variant="body2">Responder tickets sem fila</Typography>
                    <Switch
                      size="small"
                      className={classes.switchControl}
                      checked={formData.aiAnswerNoQueue}
                      onChange={(event) =>
                        updateFormData({ aiAnswerNoQueue: event.target.checked })
                      }
                      disabled={!canManageAi}
                    />
                  </div>

                  <div className={classes.docSection}>
                    <div
                      className={classes.docTitle}
                      onClick={() => setShowApiDocs((prevState) => !prevState)}
                    >
                      <ChevronDown
                        size={18}
                        strokeWidth={1.75}
                        style={{
                          transform: showApiDocs ? "rotate(180deg)" : "rotate(0deg)",
                          transition: "transform 0.3s ease",
                        }}
                      />
                      Formato de Resposta da IA
                    </div>
                    <Collapse in={showApiDocs}>
                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: "block", marginBottom: 12 }}
                      >
                        <strong>O webhook recebe (POST):</strong>
                      </Typography>
                      <div className={classes.codeBlock}>
                        <pre>{`{
  "ticketId": 123,
  "contactName": "João Silva",
  "contactNumber": "5511999999999",
  "message": "Texto da mensagem do cliente",
  "aiUserId": 5
}`}</pre>
                      </div>

                      <Typography
                        variant="caption"
                        color="textSecondary"
                        style={{ display: "block", marginBottom: 8, marginTop: 16 }}
                      >
                        <strong>Resposta esperada (JSON):</strong>
                      </Typography>
                      <div className={classes.codeBlock}>
                        <pre>{`// Enviar mensagem
{ "action": "sendMessage", "message": "Olá! Como posso ajudar?" }

// Transferir para usuário (ID do usuário)
{ "action": "transferUser", "userId": 5, "message": "Transferindo..." }

// Transferir para fila (ID da fila)
{ "action": "transferQueue", "queueId": 2, "message": "Encaminhando..." }

// Resolver/Fechar ticket
{ "action": "resolveTicket", "message": "Atendimento finalizado!" }`}</pre>
                      </div>
                    </Collapse>
                  </div>
                </div>
              </>
            ) : (
              <>
                {activeTab === "personal" && (
                  <>
                    <div className={classes.mdFieldRow}>
                      <TextField
                        label={i18n.t("userModal.form.name")}
                        variant="outlined"
                        value={formData.name}
                        onChange={(event) => updateFormData({ name: event.target.value })}
                        fullWidth
                        className={classes.mdField}
                      />
                      <ModernSelect
                        label={`${i18n.t("userModal.form.jobRole")} ${optional}`}
                        value={formData.jobRoleId}
                        onChange={(event) => updateFormData({ jobRoleId: event.target.value })}
                        MenuProps={MENU_PROPS}
                        formControlProps={{ className: classes.mdField }}
                      >
                        <MenuItem value="">{i18n.t("userModal.form.noJobRole")}</MenuItem>
                        {jobRoleMenuItems}
                      </ModernSelect>
                    </div>

                    <TextField
                      label={i18n.t("userModal.form.email")}
                      variant="outlined"
                      type="email"
                      value={formData.email}
                      onChange={(event) => updateFormData({ email: event.target.value })}
                      disabled={lockCredentials}
                      fullWidth
                      className={classes.mdField}
                    />

                    <div className={classes.mdFieldRow}>
                      <InputMask
                        mask="999.999.999-99"
                        maskChar={null}
                        value={formData.cpf}
                        onChange={(event) => updateFormData({ cpf: event.target.value })}
                      >
                        {(inputProps) => (
                          <TextField
                            {...inputProps}
                            label={`${i18n.t("userModal.form.cpf")} ${optional}`}
                            variant="outlined"
                            fullWidth
                            className={classes.mdField}
                          />
                        )}
                      </InputMask>
                    </div>
                  </>
                )}

                {activeTab === "access" && (
                  <>
                    <div className={classes.mdFieldRow}>
                      {showsAccessRole ? (
                        <ModernSelect
                          label={i18n.t("accessControl.panel.role")}
                          value={accessRole}
                          onChange={(event) => setAccessRole(event.target.value)}
                          disabled={isSelf || !!memberAccess?.readOnly}
                          helperText={isSelf ? i18n.t("accessControl.readOnly.own") : roleHelp(accessRole)}
                          MenuProps={MENU_PROPS}
                          formControlProps={{ className: classes.mdField }}
                        >
                          {["agent", "supervisor", "admin"].map((role) => (
                            <MenuItem key={role} value={role}>
                              {roleLabel(role)}
                            </MenuItem>
                          ))}
                        </ModernSelect>
                      ) : (
                        <ModernSelect
                          label={i18n.t("userModal.form.profile")}
                          value={formData.profile}
                          onChange={(event) => updateFormData({ profile: event.target.value })}
                          disabled={!canManageAccess || isSelf}
                          helperText={isSelf ? i18n.t("accessControl.readOnly.own") : undefined}
                          MenuProps={MENU_PROPS}
                          formControlProps={{ className: classes.mdField }}
                        >
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="user">User</MenuItem>
                        </ModernSelect>
                      )}
                      <TextField
                        label={i18n.t("userModal.form.password")}
                        variant="outlined"
                        type="password"
                        value={formData.password}
                        onChange={(event) => updateFormData({ password: event.target.value })}
                        disabled={lockCredentials}
                        fullWidth
                        className={classes.mdField}
                        placeholder={
                          selectedUser ? i18n.t("userModal.form.passwordKeepHint") : ""
                        }
                      />
                    </div>

                    <ModernSelect
                      label={i18n.t("userModal.form.accessScope")}
                      value={formData.accessScope || "full"}
                      onChange={(event) =>
                        updateFormData({ accessScope: event.target.value })
                      }
                      disabled={(!canManageAccess && !!selectedUser) || isSelf}
                      MenuProps={MENU_PROPS}
                      formControlProps={{ className: classes.mdField }}
                    >
                      <MenuItem value="full" disabled={internalLocked || !licenseAllowed("full")}>
                        {i18n.t("userModal.form.accessScopeFull")}
                      </MenuItem>
                      <MenuItem value="attendance" disabled={!licenseAllowed("attendance")}>
                        {i18n.t("userModal.form.accessScopeAttendance")}
                      </MenuItem>
                      <MenuItem value="internal" disabled={internalLocked || !licenseAllowed("internal")}>
                        {i18n.t("userModal.form.accessScopeInternal")}
                      </MenuItem>
                    </ModernSelect>
                    {internalLocked && (
                      <Typography className={classes.internalLockedNotice}>
                        {i18n.t("userModal.form.internalLockedNotice")}
                      </Typography>
                    )}

                    {accessRoles && memberAccess && !isSelf && (
                      <button type="button" className={classes.customizeLink} onClick={openPermissions}>
                        <KeyRound size={14} strokeWidth={1.8} aria-hidden="true" />
                        {i18n.t("userModal.form.customizePermissions")}
                      </button>
                    )}

                    {showMfaCard && (
                      <div className={classes.mfaCard}>
                        <div className={classes.mfaCardLeft}>
                          <div
                            className={`${classes.mfaIconBadge} ${
                              selectedUser.mfaEnabled
                                ? classes.mfaIconBadgeOn
                                : classes.mfaIconBadgeOff
                            }`}
                          >
                            {selectedUser.mfaEnabled ? (
                              <ShieldCheck size={20} strokeWidth={1.75} />
                            ) : (
                              <ShieldOff size={20} strokeWidth={1.75} />
                            )}
                          </div>
                          <div className={classes.mfaTextWrap}>
                            <span className={classes.mfaTitle}>
                              {i18n.t("mfa.profile.menuItem")}
                            </span>
                            <span
                              className={`${classes.mfaStatus} ${
                                selectedUser.mfaEnabled
                                  ? classes.mfaStatusOn
                                  : classes.mfaStatusOff
                              }`}
                            >
                              {selectedUser.mfaEnabled
                                ? selectedUser.mfaEnabledAt
                                  ? i18n.t("mfa.profile.statusEnabled", {
                                      date: new Date(selectedUser.mfaEnabledAt).toLocaleDateString(),
                                    })
                                  : i18n.t("mfa.profile.statusEnabled", { date: "—" })
                                : i18n.t("mfa.profile.statusDisabled")}
                            </span>
                          </div>
                        </div>
                        {selectedUser.mfaEnabled && (
                          <Button
                            size="small"
                            variant="outlined"
                            className={classes.mfaResetBtn}
                            startIcon={<RotateCcw size={14} strokeWidth={2} />}
                            onClick={async () => {
                              if (!window.confirm(i18n.t("mfa.profile.adminResetConfirm"))) return;
                              try {
                                await api.post(`/users/${selectedUser.id}/mfa/reset`);
                                toast.success(i18n.t("mfa.profile.adminResetDone"));
                                if (onSaved) onSaved();
                              } catch (err) {
                                toastError(err);
                              }
                            }}
                          >
                            {i18n.t("mfa.profile.adminReset")}
                          </Button>
                        )}
                      </div>
                    )}
                  </>
                )}

                {activeTab === "service" && (
                  <>
                    <div className={classes.mdFieldRow}>
                      <ModernSelect
                        label={i18n.t("userModal.form.whatsapp")}
                        value={formData.whatsappId}
                        onChange={(event) =>
                          updateFormData({ whatsappId: event.target.value })
                        }
                        MenuProps={MENU_PROPS}
                        formControlProps={{ className: classes.mdField }}
                      >
                        <MenuItem value="">{i18n.t("userModal.form.noWhatsapp")}</MenuItem>
                        {whatsappMenuItems}
                      </ModernSelect>
                      {accessRoles ? (
                        <div className={`${classes.mdField} ${classes.switchContainer}`}>
                          <div>
                            <Typography variant="body2" id="user-editor-counts-in-indicators">
                              {i18n.t("userModal.form.countsInIndicators")}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {i18n.t("userModal.form.countsInIndicatorsHelp")}
                            </Typography>
                          </div>
                          <Switch
                            size="small"
                            className={classes.switchControl}
                            checked={!!formData.isAttendant}
                            onChange={(event) => updateFormData({ isAttendant: event.target.checked })}
                            inputProps={{ "aria-labelledby": "user-editor-counts-in-indicators" }}
                          />
                        </div>
                      ) : (
                        <ModernSelect
                          label={i18n.t("userModal.form.operationalType")}
                          value={formData.isAttendant ? "atendente" : "supervisor"}
                          onChange={(event) =>
                            updateFormData({
                              isAttendant: event.target.value === "atendente",
                            })
                          }
                          MenuProps={MENU_PROPS}
                          formControlProps={{ className: classes.mdField }}
                        >
                          <MenuItem value="atendente">
                            {i18n.t("userModal.form.attendant")}
                          </MenuItem>
                          <MenuItem value="supervisor">
                            {i18n.t("userModal.form.supervisor")}
                          </MenuItem>
                        </ModernSelect>
                      )}
                    </div>

                    <div>
                      <span className={classes.mdFieldLabel}>
                        {i18n.t("userModal.form.queues")}
                      </span>
                      <div className={classes.chipsWrap}>
                        {selectedQueues.length === 0 && (
                          <span className={classes.noQueuesText}>
                            {i18n.t("userModal.form.noQueuesSelected")}
                          </span>
                        )}
                        {selectedQueues.map(({ id, queue }) => (
                          <Chip
                            key={id}
                            size="small"
                            className={classes.queueChip}
                            icon={
                              <span
                                className={classes.chipDot}
                                style={{ backgroundColor: queue?.color || "#94a3b8" }}
                              />
                            }
                            label={queue?.name || `#${id}`}
                            onDelete={() =>
                              updateFormData({
                                queueIds: formData.queueIds.filter((x) => x !== id),
                              })
                            }
                          />
                        ))}
                        <Button
                          className={classes.addQueueBtn}
                          onClick={(e) => setQueueMenuAnchor(e.currentTarget)}
                          startIcon={<Plus size={14} strokeWidth={2} />}
                        >
                          {i18n.t("userModal.form.addQueue")}
                        </Button>
                      </div>
                      {/* Menu multi-seleção: clicar alterna o setor SEM fechar
                          (fecha só fora/Esc), permitindo marcar vários de uma vez. */}
                      <Menu
                        anchorEl={queueMenuAnchor}
                        open={Boolean(queueMenuAnchor)}
                        onClose={() => setQueueMenuAnchor(null)}
                        getContentAnchorEl={null}
                        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                        classes={{ paper: classes.queueMenuPaper, list: classes.queueMenuList }}
                      >
                        {queues.length === 0 ? (
                          <MenuItem disabled>
                            {i18n.t("userModal.form.noQueueAvailable")}
                          </MenuItem>
                        ) : (
                          [
                            <MenuItem
                              key="__all__"
                              className={classes.queueMenuAllItem}
                              disabled={allQueuesSelected}
                              onClick={() =>
                                setFormData((prev) => ({
                                  ...prev,
                                  queueIds: queues.map((q) => q.id),
                                }))
                              }
                            >
                              <CheckCheck size={15} strokeWidth={2} />
                              {i18n.t("userModal.form.addAllQueues")}
                            </MenuItem>,
                            ...queues.map((q) => {
                              const isSelected = formData.queueIds.includes(q.id);
                              return (
                                <MenuItem
                                  key={q.id}
                                  className={isSelected ? classes.queueMenuItemSelected : ""}
                                  onClick={() => toggleQueueId(q.id)}
                                >
                                  <span
                                    className={classes.chipDot}
                                    style={{ backgroundColor: q.color || "#94a3b8", marginLeft: 0 }}
                                  />
                                  <span className={classes.queueMenuItemLabel}>{q.name}</span>
                                  {isSelected && (
                                    <Check
                                      size={14}
                                      strokeWidth={2.5}
                                      className={classes.queueMenuCheck}
                                    />
                                  )}
                                </MenuItem>
                              );
                            }),
                          ]
                        )}
                      </Menu>
                    </div>

                    {(showMaxCompanies || showGroupCompanies) && (
                      <div className={classes.mdFieldRow}>
                        {showMaxCompanies && (
                          <TextField
                            label={i18n.t("userModal.form.maxCompanies")}
                            type="number"
                            variant="outlined"
                            className={classes.mdField}
                            inputProps={{ min: 1, max: 50 }}
                            value={userMaxCompanies}
                            onChange={handleMaxCompaniesChange}
                            helperText={i18n.t("userModal.form.maxCompaniesHelper")}
                          />
                        )}
                        {showGroupCompanies && (
                          <ModernSelect
                            label={i18n.t("userModal.form.groupCompanies")}
                            multiple
                            value={userCompanyIds}
                            onChange={handleCompaniesChange}
                            renderValue={(selected) =>
                              availableCompanies
                                .filter((company) => selected.includes(company.id))
                                .map((company) => company.name)
                                .join(", ")
                            }
                            MenuProps={MENU_PROPS}
                            formControlProps={{ className: classes.mdField }}
                          >
                            {companyMenuItems}
                          </ModernSelect>
                        )}
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </>
      )}

      {/* ── Rodapé fixo ── */}
      <div className={classes.userFooter}>
        <div className={classes.footerDirty}>
          <UnsavedChangesHint show={!!selectedUser && isDirty} />
        </div>
        <div className={classes.footerActions}>
          <Button
            onClick={handleClose}
            disabled={saving}
            className={`${classes.btnBase} ${classes.btnCancel}`}
            startIcon={<X size={16} strokeWidth={1.75} />}
          >
            {i18n.t("userModal.buttons.cancel")}
          </Button>
          <Button
            onClick={handleSaveUser}
            disabled={saving || loadingData || (!!selectedUser && !isDirty)}
            className={`${classes.btnBase} ${classes.btnPrimary}`}
            startIcon={
              saving ? (
                <CircularProgress size={14} className={classes.spinner} />
              ) : selectedUser ? (
                <Pencil size={16} strokeWidth={1.75} />
              ) : (
                <Plus size={16} strokeWidth={1.75} />
              )
            }
          >
            {selectedUser
              ? i18n.t("userModal.buttons.saveChanges")
              : i18n.t("userModal.buttons.okAdd")}
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

export default UserEditorModal;
